/**
 * Find Time walker — resolves a user's mode selection into a
 * concrete list of `YYYY-MM-DD` date keys.
 *
 * **Anchor is always today**, never the visible calendar week. That
 * way clicking "Find time this week" while navigated to some future
 * week still targets the user's actual current week; the container
 * then navigates the calendar to the first resolved date.
 *
 * Inputs:
 *   mode      — the Find Time option the user picked.
 *   nowMs     — current time (tests pass a frozen epoch).
 *   timezone  — drives day boundaries.
 *
 * Output:
 *   `{ dateKeys, firstDateKey }` — dateKeys are what `applyFindTime`
 *   dispatches `toggleDay(true)` on. `firstDateKey` drives the
 *   calendar's `gotoDate` when the selection spans a future week.
 *
 * ## Why no availability oracle?
 *
 * Legacy walked availability forward to skip no-availability days.
 * We skip that filter — it couples Find Time to the visible week's
 * pipeline cache, which fails once the user navigates. Instead we
 * pick target dates unconditionally; if a day has no generator
 * output, the merger simply emits nothing for it. The user sees
 * the calendar navigate to the chosen week, and whatever days are
 * actually unavailable render as empty columns — same correctness,
 * no availability-lookup fragility.
 */
import moment from 'moment-timezone';
import type { FindTimeValue } from './modes';
import { weekdaysTargetFor } from './modes';

// ─────────────────────────────────────────────────────────────────
// Tuning constants.
// ─────────────────────────────────────────────────────────────────

/**
 * `0 = Sun, 1 = Mon, … 6 = Sat`. A day is a "weekday" iff its
 * `day()` value is in this set. Mon–Fri.
 */
const WEEKDAY_DOW = new Set<number>([1, 2, 3, 4, 5]);

// ─────────────────────────────────────────────────────────────────
// Small helpers.
// ─────────────────────────────────────────────────────────────────

function dateKey(m: moment.Moment): string {
  return m.format('YYYY-MM-DD');
}

function isWeekday(m: moment.Moment): boolean {
  return WEEKDAY_DOW.has(m.day());
}

// ─────────────────────────────────────────────────────────────────
// Public API.
// ─────────────────────────────────────────────────────────────────

export interface WalkArgs {
  mode: Exclude<FindTimeValue, null>;
  nowMs: number;
  timezone: string;
}

export interface WalkResult {
  /** Dates to auto-fill. */
  dateKeys: string[];
  /** Earliest date — drives calendar navigation. */
  firstDateKey: string | null;
}

export function walkFindTime({ mode, nowMs, timezone }: WalkArgs): WalkResult {
  if (mode === 'this_week') {
    return walkThisWeek(nowMs, timezone);
  }
  const weekdaysTarget = weekdaysTargetFor(mode);
  if (weekdaysTarget !== null) {
    return walkNextWeekdays(nowMs, timezone, weekdaysTarget);
  }
  if (mode === 'next_week') {
    return walkCalendarWeeks(nowMs, timezone, 1);
  }
  if (mode === 'next_2_weeks') {
    return walkCalendarWeeks(nowMs, timezone, 2);
  }
  return { dateKeys: [], firstDateKey: null };
}

// ─────────────────────────────────────────────────────────────────
// Walker — "this week".
//
// Today (inclusive) through the last weekday of the current ISO
// week. If today is already Fri/Sat/Sun, returns just today+Fri
// depending on which it is — matches legacy "fill remaining
// weekdays this week, including today if it's a weekday".
// ─────────────────────────────────────────────────────────────────

function walkThisWeek(nowMs: number, timezone: string): WalkResult {
  const today = moment.tz(nowMs, timezone).startOf('day');
  const weekEnd = today.clone().endOf('isoWeek'); // Sunday end
  const dateKeys: string[] = [];
  const cursor = today.clone();
  while (cursor.isSameOrBefore(weekEnd, 'day')) {
    if (isWeekday(cursor)) dateKeys.push(dateKey(cursor));
    cursor.add(1, 'day');
  }
  return { dateKeys, firstDateKey: dateKeys[0] ?? null };
}

// ─────────────────────────────────────────────────────────────────
// Walker — "next N weekdays".
//
// Tomorrow onwards, pick the next N weekdays (Mon–Fri). No
// availability filter — the pipeline decides what to render.
// ─────────────────────────────────────────────────────────────────

function walkNextWeekdays(
  nowMs: number,
  timezone: string,
  target: number
): WalkResult {
  const cursor = moment.tz(nowMs, timezone).add(1, 'day').startOf('day');
  const dateKeys: string[] = [];
  while (dateKeys.length < target) {
    if (isWeekday(cursor)) dateKeys.push(dateKey(cursor));
    cursor.add(1, 'day');
  }
  return { dateKeys, firstDateKey: dateKeys[0] ?? null };
}

// ─────────────────────────────────────────────────────────────────
// Walker — "next N calendar weeks".
//
// Monday of next ISO-week → weeks × 7 days. Picks every day,
// including weekends. Pipeline filters by interviewer availability
// when rendering.
// ─────────────────────────────────────────────────────────────────

function walkCalendarWeeks(
  nowMs: number,
  timezone: string,
  weeks: number
): WalkResult {
  const firstWeekStart = moment
    .tz(nowMs, timezone)
    .add(1, 'week')
    .startOf('isoWeek');
  const dateKeys: string[] = [];
  for (let w = 0; w < weeks; w += 1) {
    const weekStart = firstWeekStart.clone().add(w, 'week');
    for (let d = 0; d < 7; d += 1) {
      dateKeys.push(dateKey(weekStart.clone().add(d, 'day')));
    }
  }
  return { dateKeys, firstDateKey: dateKeys[0] ?? null };
}
